import * as ents from '@attain-apps/entities'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type * as fileMgmt from '../../../../../../file-mgmt'
import type { Context } from '../../../types'
import type { Plugin } from '../plugin'

/**
 * Effect that handles sending messages to the agent with file uploads
 */
export const sendMessage = store.effects.create(
  (
    feature: Plugin['feature'],
    plugin: Plugin,
    fileMgmtFeature: fileMgmt.Feature,
  ) =>
    feature.actions.sendMessage.request.observe().pipe(
      rxjs.switchMap(request =>
        plugin.collection.feature.selectors.context.asObservable.pipe(
          rxjs.take(1),
          rxjs.filter(TypeChecker.isNotNullish.raw),
          rxjs.switchMap(context => {
            const { files, content } = request.data
            const fileNames = files.map(file => file.name)
            const info = _buildInfoEntries({ content, fileNames })

            const uploads$ = _uploadFilesHandler$(fileMgmtFeature, {
              context,
              conversationId: request.data.conversationId,
              files,
              messageId: request.data.messageId,
            })

            return uploads$.pipe(
              rxjs.switchMap(() =>
                plugin.api
                  .sendMessage(context, {
                    agentId: plugin.assistantGroup.selectedAgentIdSg(),
                    conversationId: request.data.conversationId,
                    info,
                    knowledgeBaseRetrieval: request.data.knowledgeBaseRetrieval,
                    messageId: request.data.messageId,
                    workspaceId: request.data.workspaceId,
                  })
                  .pipe(
                    rxjs.map(() =>
                      feature.actions.sendMessage.success
                        .from('sendMessage (effect)')
                        .create({ input: request.data }),
                    ),
                  ),
              ),
            )
          }),
          rxjs.catchError((error: Error) =>
            rxjs.of(
              // The agent runs inside the request, so a long turn outlasts
              // AppSync's 30 seconds while carrying on to completion. Its
              // outcome arrives through the conversation's subscriptions,
              // failure included, so the message counts as sent.
              graphql.GraphqlResultError.isExecutionTimeout(error)
                ? feature.actions.sendMessage.success
                    .from('sendMessage (effect)')
                    .create({ input: request.data })
                : feature.actions.sendMessage.error
                    .from('sendMessage (effect)')
                    .create({ error, input: request.data }),
            ),
          ),
        ),
      ),
    ),
)

/**
 * Builds the typed `Info[]` from raw user input.
 *
 * The user's text becomes a single `text` entry; each uploaded file name
 * becomes a `file` entry. All entries share a single timestamp captured at
 * build time.
 */
export function _buildInfoEntries(params: {
  content: string
  fileNames: readonly string[]
}): readonly ents.core.agent.Info[] {
  const createdAt = new Date()
  const entries: ents.core.agent.Info[] = []
  if (params.content.length > 0) {
    entries.push({ createdAt, text: params.content, type: 'text' })
  }
  for (const name of params.fileNames) {
    entries.push({ createdAt, name, type: 'file' })
  }
  return entries
}

/**
 * Uploads files in parallel, returns immediately if no files provided
 */
function _uploadFilesHandler$(
  fileMgmtFeature: fileMgmt.Feature,
  params: {
    context: NonNullable<Context>
    files: File[]
    conversationId: ents.core.agent.Conversation.Id
    messageId: ents.core.agent.Message['id']
  },
): rxjs.Observable<unknown> {
  const { context, files, conversationId, messageId } = params

  if (files.length === 0) {
    return rxjs.of(undefined)
  }

  return rxjs.forkJoin(
    files.map(file =>
      fileMgmtFeature.actions.upload
        .from('sendMessage (effect)')
        .execute({
          context,
          file,
          purpose: ents.core.common.fileManagement.Purpose.FileManagement,
          qualifier: ents.core.agent.Message.File.generateQualifier({
            conversation: conversationId,
            message: messageId,
          }),
        })
        .asObservable.pipe(rxjs.take(1)),
    ),
  )
}
